import { FieldTypes } from 'constants/field-types-enum';

export const operatorTypes = [
  {
    value: 'is',
    label: 'Equal to',
    symbol: '=',
    allowedTypes: [FieldTypes.MultipleChoice, FieldTypes.YesNo, FieldTypes.Country],
  },
  {
    value: 'is_not',
    label: 'Not equal to',
    symbol: '≠',
    allowedTypes: [FieldTypes.MultipleChoice, FieldTypes.YesNo, FieldTypes.Country],
  },
  {
    value: 'lower_than',
    label: 'Lower than',
    symbol: '<',
    allowedTypes: [FieldTypes.OpinionScale, FieldTypes.StarRating, FieldTypes.Date],
  },
  {
    value: 'lower_equal_than',
    label: 'Lower or equal than',
    symbol: '≤',
    allowedTypes: [FieldTypes.OpinionScale, FieldTypes.StarRating, FieldTypes.Date],
  },
  {
    value: 'greater_than',
    label: 'Greater than',
    symbol: '>',
    allowedTypes: [FieldTypes.OpinionScale, FieldTypes.StarRating, FieldTypes.Date],
  },
  {
    value: 'greater_equal_than',
    label: 'Greater or equal than',
    symbol: '≥',
    allowedTypes: [FieldTypes.OpinionScale, FieldTypes.StarRating, FieldTypes.Date],
  },
];

export const OPERATOR_SYMBOLS = Object.fromEntries(operatorTypes.map(op => [op.value, op.label]));

export const typesWithLogic = [
  FieldTypes.MultipleChoice,
  FieldTypes.Date,
  FieldTypes.YesNo,
  FieldTypes.OpinionScale,
  FieldTypes.StarRating,
  FieldTypes.Country,
];

/**
 * watchFn is called with the trigger field id as a string (e.g. "123"), so consumers
 * should look up answers using string keys for consistency with RHF and API payloads.
 */
export const getIgnoredFieldsFromLogicJumps = (logicJumps, fields, watchFn, maxOrder) => {
  let ignoredFields = [];

  const logicByField = (logicJumps || []).reduce((acc, logic) => {
    if (!acc[logic.field]) acc[logic.field] = [];
    acc[logic.field].push(logic);
    return acc;
  }, {});

  Object.entries(logicByField || {}).forEach(([fieldOrder, rules]) => {
    const field = fields.find(f => f.order === Number(fieldOrder));
    if (!field) return;

    const value = watchFn(String(field.id));
    const hasAnswer =
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0);

    const conditionalRules = rules.filter(r => r.operator !== 'finish' && r.operator !== 'always');
    const fallbackRules = rules.filter(r => r.operator === 'finish' || r.operator === 'always');

    if (!hasAnswer && conditionalRules?.length > 0) {
      if (fallbackRules?.length > 0) {
        // Fallback handles "no matching condition" — apply it (unchanged behaviour)
        fallbackRules.forEach(rule => {
          ignoredFields = [...ignoredFields, ...getIgnoredFields(rule, value, maxOrder, fields)];
        });
      } else {
        // No fallback: hide everything any conditional rule could hide so branch questions
        // don't appear until the controlling question is answered.
        for (const rule of conditionalRules) {
          ignoredFields = [
            ...ignoredFields,
            ...getIgnoredFields({ ...rule, operator: 'always' }, value, maxOrder, fields),
          ];
        }
      }
    } else {
      const matchedConditional = conditionalRules.filter(r => meetsLogic(r, value));
      const matchedRules = matchedConditional.length > 0 ? matchedConditional : fallbackRules;
      matchedRules.forEach(rule => {
        ignoredFields = [...ignoredFields, ...getIgnoredFields(rule, value, maxOrder, fields)];
      });
    }
  });

  return [...new Set(ignoredFields)];
};

// Set of field orders that exist (used when to is -1 / "finish form" to avoid including gap orders)
const existingOrdersSet = fields => new Set((fields || []).map(f => Number(f.order)));

// Logic
export const getIgnoredFields = (logic, value, maxOrder = null, fields = null) => {
  let ignoredFields = [];
  if (!meetsLogic(logic, value)) return ignoredFields;

  const existingOrders = fields ? existingOrdersSet(fields) : null;

  const fromNum = Number(logic.from);
  const toNum = Number(logic.to);
  if (toNum === -1) {
    if (maxOrder != null) {
      for (let i = fromNum + 1; i <= maxOrder; i++) {
        if (existingOrders == null || existingOrders.has(i)) {
          ignoredFields.push(i);
        }
      }
    }
  } else {
    for (let i = fromNum + 1; i < toNum; i++) {
      if (existingOrders == null || existingOrders.has(i)) {
        ignoredFields.push(i);
      }
    }
  }
  return ignoredFields;
};

export const meetsLogic = (rule, value) => {
  // Fallback operators always trigger regardless of the current answer (must be before value check)
  if (rule.operator === 'finish' || rule.operator === 'always') return true;

  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;

  const ruleValue = isNaN(rule.value) || rule.value === '' ? rule.value : Number(rule.value);

  const compareValue = isNaN(value) || value === '' ? value : Number(value);

  const isDateComparison = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

  switch (rule.operator) {
    case 'is':
      if (Array.isArray(value)) return value.includes(rule.value);
      if (!isNaN(value) && !isNaN(rule.value) && value !== '' && rule.value !== '') {
        return Number(value) === Number(rule.value);
      }
      return value === rule.value;
    case 'is_not':
      if (Array.isArray(value)) return !value.includes(rule.value);
      if (!isNaN(value) && !isNaN(rule.value) && value !== '' && rule.value !== '') {
        return Number(value) !== Number(rule.value);
      }
      return value !== rule.value;
    case 'lower_than':
      return isDateComparison ? value < rule.value : compareValue < ruleValue;
    case 'lower_equal_than':
      return isDateComparison ? value <= rule.value : compareValue <= ruleValue;
    case 'greater_than':
      return isDateComparison ? value > rule.value : compareValue > ruleValue;
    case 'greater_equal_than':
      return isDateComparison ? value >= rule.value : compareValue >= ruleValue;
    default:
      console.warn(`Unknown operator: ${rule.operator}`);
      return false;
  }
};
